import i18n from '@/locale';
import { CatalogItem } from '@/types/workflow';
import Message from '@/utils/message';

type TargetContext = '_self' | '_parent' | '_blank' | '_top';

export const openWindow = (
  url: string,
  opts?: { target?: TargetContext; [key: string]: any }
) => {
  const { target = '_blank', ...others } = opts || {};
  window.open(
    url,
    target,
    Object.entries(others)
      .reduce((preValue: string[], curValue) => {
        const [key, value] = curValue;
        return [...preValue, `${key}=${value}`];
      }, [])
      .join(',')
  );
};

export const regexUrl = new RegExp(
  // eslint-disable-next-line max-len
  '^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$',
  'i'
);

/**
 * @msg: 将字符串参数转为数组
 * @param {string} parameter
 * @return {*}
 */
export const queryUrlParameter = (parameter: string) => {
  const data: any = [];
  const parameterStr = parameter.slice(1);
  const parameterArr = parameterStr.split('&');
  parameterArr.forEach((item) => {
    const name = item.split('=')[0];
    const value = item.split('=')[1];
    data.push({
      name,
      value,
    });
  });
  return data;
};

/**
 * @msg: 将参数数组转为字符串
 * @param {Array} arr
 * @return {*}
 */
export const queryString = (arr: Array<any>) => {
  arr = arr.filter((item) => item.name);
  if (arr.length < 1) return '';
  let request_parameter = '?';
  const len = arr.length;
  arr.forEach((item: any, index: number) => {
    request_parameter += `${item.name}=${item.value}`;
    if (index < len - 1) {
      request_parameter += '&';
    }
  });
  return request_parameter;
};

/**
 * @msg: 格式化时长，将纳秒数转为 xx天xx时xx分xx秒xx.xxxms（ms保留三位小数）
 * @param {number} ns - 纳秒数
 * @return {string} 格式化后的字符串
 */
export const formatDurationZh = (ns: number): string => {
  if (typeof ns !== 'number' || Number.isNaN(ns) || ns < 0) return '-';
  // 将纳秒转换为毫秒（保留三位小数）
  const ms = ns / 1000;

  const day = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hour = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minute = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  const second = Math.floor((ms % (60 * 1000)) / 1000);
  const millisecond = ms % 1000;

  let result = '';
  if (day > 0) result += `${day}d `;
  if (hour > 0 || result) result += `${hour}h `;
  if (minute > 0 || result) result += `${minute}m `;
  if (second > 0 || result) result += `${second}s `;
  // 如果 ms 是整数则不加 .000，否则保留三位小数
  result += Number.isInteger(millisecond)
    ? `${millisecond}ms`
    : `${millisecond.toFixed(3)}ms`;
  return result;
};

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    Message.success(i18n.global.t('common.copySuccess'));
  } catch (err) {
    console.warn('Clipboard API 复制失败，尝试使用备用方法:', err);
    // 备用方法: 使用 document.execCommand('copy')
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // 避免在页面上可见或引起滚动
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        Message.success(i18n.global.t('common.copySuccess'));
      } else {
        throw new Error('document.execCommand(copy) returned false');
      }
    } catch (fallbackErr) {
      console.error('备用复制方法失败:', fallbackErr);
      Message.error(i18n.global.t('common.copyFailed'));
    } finally {
      document.body.removeChild(textarea);
    }
  }
};

// 自动累加版本号的方法，基于当前版本号字符串（如 v1.0.0），返回下一个版本号（v1.0.1），到99自动进位
export const getNextVersion = (currentVersion: string): string => {
  // 匹配 v1.0.0 这种格式
  const match = currentVersion.match(/^v(\d+)\.(\d+)\.(\d+)$/);
  if (!match) {
    // 如果格式不对，返回默认初始版本
    return 'v1.0.0';
  }
  let [major, minor, patch] = match.slice(1).map(Number);
  patch += 1;
  if (patch > 99) {
    patch = 0;
    minor += 1;
    if (minor > 99) {
      minor = 0;
      major += 1;
    }
  }
  return `v${major}.${minor}.${patch}`;
};

/**
 * @msg: 格式化相对时间
 * @param {string | Date} dateInput - 日期字符串或Date对象
 * @return {string} 相对时间字符串（如：3天前、15分钟前、2个月前）
 */
export const formatRelativeTime = (dateInput: string | Date): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // 如果时间差为负数，说明是未来时间
  if (diffMs < 0) {
    return i18n.global.t('common.time.justNow');
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears > 0) {
    return i18n.global.t('common.time.yearsAgo', { count: diffYears });
  }
  if (diffMonths > 0) {
    return i18n.global.t('common.time.monthsAgo', { count: diffMonths });
  }
  if (diffDays > 0) {
    return i18n.global.t('common.time.daysAgo', { count: diffDays });
  }
  if (diffHours > 0) {
    return i18n.global.t('common.time.hoursAgo', { count: diffHours });
  }
  if (diffMinutes > 0) {
    return i18n.global.t('common.time.minutesAgo', { count: diffMinutes });
  }
  if (diffSeconds > 30) {
    return i18n.global.t('common.time.secondsAgo', { count: diffSeconds });
  }
  return i18n.global.t('common.time.justNow');
};

/**
 * @msg: 格式化目录列表
 * @param list
 * @returns
 */
export const formatCatalogList = (list: string[]) => {
  if (!list || !Array.isArray(list) || list.length === 0) return [];
  const buildTreeData = (pathList: string[]): CatalogItem[] => {
    const root: CatalogItem[] = [];

    pathList.forEach((path) => {
      const parts = path.split('/');
      let current = root;
      let fullPath = '';

      parts.forEach((part) => {
        fullPath = fullPath ? `${fullPath}/${part}` : part;
        let node = current.find((n) => n.key === fullPath);

        if (!node) {
          node = {
            key: fullPath,
            title: part,
            children: [],
          };
          current.push(node);
        }

        current = node.children!;
      });
    });

    return root;
  };
  return buildTreeData(list);
};

export default null;
